"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { profileHref } from "@/lib/profile-url"
import { useDefaultProfileImages } from "@/lib/default-profile-images"

export type PeopleScope = "scriptors" | "storefronts" | "goonpals" | "goonfolios"

export interface PersonHit {
  id: string
  username: string
  display_name: string | null
  short_code: string | null
  avatar_url: string | null
  followers: number
  store_slug: string | null
  /** How much of what this place is about they have (scripts, Goonpals, Goonfolios, released items). */
  content_count: number
}

/** Where tapping a person goes, per place they were found. */
export function personHref(scope: PeopleScope, p: PersonHit): string {
  switch (scope) {
    case "storefronts":
      return p.store_slug ? `/store/${p.store_slug}` : profileHref(p.short_code, p.username, p.id)
    case "goonpals":
      return `/goonpals/creator/${encodeURIComponent(p.username)}`
    case "goonfolios":
      return `${profileHref(p.short_code, p.username, p.id)}?tab=collections`
    default:
      return profileHref(p.short_code, p.username, p.id)
  }
}

/** What each place calls the people in its list, and how it counts what they have. */
const COPY: Record<PeopleScope, { heading: string; one: string; many: string }> = {
  scriptors: { heading: "Scriptors", one: "script", many: "scripts" },
  storefronts: { heading: "Creators with a store", one: "item", many: "items" },
  goonpals: { heading: "Goonpal creators", one: "Goonpal", many: "Goonpals" },
  goonfolios: { heading: "Goonfolio creators", one: "Goonfolio", many: "Goonfolios" },
}

/** Fetch up to this many; the list shows about five rows and scrolls for the rest. */
const FETCH_LIMIT = 20
/** The list's own height cap, and the space the heading row and gap take around it. */
const LIST_MAX_PX = 240
const CHROME_PX = 44
/** Never squeeze the list below this (about two rows). */
const LIST_MIN_PX = 96

/**
 * A floating list of matching people under a search box. Put it inside a `relative` wrapper that holds the input.
 *  - It overlays (never pushes the page) and scrolls: about five rows are visible, up to 20 are loaded, and the
 *    height is capped to the screen so it still fits a phone with the keyboard up.
 *  - It opens upward when there is no room below.
 *  - It only lists people who have what the place is about (see /api/users/search scopes).
 */
export function PeopleSearchDropdown({
  query,
  scope,
  open,
  className,
}: {
  query: string
  scope: PeopleScope
  /** Normally "the input is focused". */
  open: boolean
  className?: string
}) {
  const [hits, setHits] = useState<PersonHit[]>([])
  const [openUp, setOpenUp] = useState(false)
  // The list's height for the room actually available around the box (a phone with the keyboard up has little).
  const [listMax, setListMax] = useState(LIST_MAX_PX)
  const rootRef = useRef<HTMLDivElement>(null)
  const { avatarUrl: defaultAvatarUrl } = useDefaultProfileImages()

  useEffect(() => {
    const q = query.replace(/^@+/, "").trim()
    if (q.length < 1) {
      setHits([])
      return
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      fetch(`/api/users/search?q=${encodeURIComponent(q)}&scope=${scope}&limit=${FETCH_LIMIT}`, { signal: controller.signal })
        .then((r) => (r.ok ? r.json() : { users: [] }))
        .then((d) => setHits(Array.isArray(d.users) ? d.users : []))
        .catch(() => {})
    }, 250)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, scope])

  // Fit the list to the space around the box: down when it fits, otherwise up when there is more room above, and
  // in either case shrink the list to what is available so it is never cut off by the screen edge.
  useEffect(() => {
    if (!open || hits.length === 0) return
    const place = () => {
      const parent = rootRef.current?.parentElement
      if (!parent) return
      const rect = parent.getBoundingClientRect()
      const cap = Math.min(LIST_MAX_PX, window.innerHeight * 0.45)
      const below = window.innerHeight - rect.bottom - 8
      const above = rect.top - 8
      if (below >= cap + CHROME_PX) {
        setOpenUp(false)
        setListMax(cap)
      } else if (above > below) {
        setOpenUp(true)
        setListMax(Math.max(LIST_MIN_PX, Math.min(cap, above - CHROME_PX)))
      } else {
        setOpenUp(false)
        setListMax(Math.max(LIST_MIN_PX, Math.min(cap, below - CHROME_PX)))
      }
    }
    place()
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => {
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", place, true)
    }
  }, [open, hits.length])

  if (!open || hits.length === 0) return null

  const copy = COPY[scope]

  return (
    <div
      ref={rootRef}
      className={cn(
        "absolute left-0 right-0 z-50 overflow-hidden rounded-lg border border-border/60 bg-popover shadow-lg",
        openUp ? "bottom-full mb-1" : "top-full mt-1",
        className,
      )}
    >
      <p className="border-b border-border/40 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {copy.heading}
        {hits.length > 5 && <span className="ml-1 normal-case tracking-normal">· {hits.length}{hits.length >= FETCH_LIMIT ? "+" : ""}</span>}
      </p>
      {/* About five rows; the cap is worked out from the room around the box (see the effect above). */}
      <div
        className="overflow-y-auto overscroll-contain p-1"
        style={{ maxHeight: listMax, touchAction: "pan-y" }}
      >
        {hits.map((p) => {
          const count = p.content_count
          return (
            <Link
              key={p.id}
              href={personHref(scope, p)}
              onMouseDown={(e) => e.preventDefault()}
              className="flex min-h-11 items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/60 active:bg-muted"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={p.avatar_url || defaultAvatarUrl}
                alt=""
                onError={(e) => {
                  const img = e.currentTarget
                  if (img.src !== defaultAvatarUrl) img.src = defaultAvatarUrl
                }}
                className="h-8 w-8 shrink-0 rounded-full bg-muted object-cover"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{p.display_name || p.username}</span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  @{p.username} · {p.followers.toLocaleString()} {p.followers === 1 ? "follower" : "followers"}
                </span>
              </span>
              {count > 0 && (
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  {count.toLocaleString()} {count === 1 ? copy.one : copy.many}
                </span>
              )}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
